// ListSelection.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Select,
  Form,
  Row,
  Col,
  message,
  Divider,
  Alert
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { getListSegments, getProducts, getCategories } from '../../core/api/report';
import { fetchBrandsBySelectedClients } from '../../core/api/settings';
import { getLightweightMediaPlans } from '../../core/api/mediaplan';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import SelectedSegmentSummary from './SelectedSegmentSummary';
const { Option } = Select;

const ListSelection = () => {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Search state for table filtering
  // Search state for table filtering - used by getColumnSearchProps
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  
  // State to force SelectedSegmentSummary to update
  const [updateCounter, setUpdateCounter] = useState(0);
  const [dateRange, setDateRange] = useState([]);
  const [product, setProduct] = useState(undefined);
  const [category, setCategory] = useState(undefined);
  const [performanceField, setPerformanceField] = useState('net_profit_loss');
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(undefined);
  const [loadingBrands, setLoadingBrands] = useState(false);
  
  // Campaign and list selection states
  const [campaigns, setCampaigns] = useState([]);
  const [availableLists, setAvailableLists] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingLists, setLoadingLists] = useState(false);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState(undefined); // Currently active campaign for list selection
  const [campaignListSelections, setCampaignListSelections] = useState({});
    
  // Options for dropdowns
  const [productOptions, setProductOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  
  // Performance field options
  const performanceOptions = [
    { label: 'Net Profit/Loss', value: 'net_profit_loss' },
    { label: 'Profit/Loss per Buyer', value: 'profit_loss_per_buyer' },
    { label: 'Net Profit/Loss per Mail', value: 'net_profit_loss_per_mail' },
  ];

  // Redux selections
  const selectedClients = useSelector((state) => state.selectedClients);
  const buildFilters = useCallback(() => {
    const filters = {
      client_ids: selectedClients,
    };

    if (dateRange?.[0] && dateRange?.[1]) {
      filters.start_date = dayjs(dateRange[0]).format('YYYY-MM-DD');
      filters.end_date = dayjs(dateRange[1]).format('YYYY-MM-DD');
    }
    
    // Add new filters
    if (product) filters.product = product;
    if (category) filters.category = category;
    if (performanceField) filters.performance_field = performanceField;
    if (selectedBrand) filters.brand_ids = [selectedBrand];
    return filters;
  }, [selectedClients, dateRange, product, category, performanceField, selectedBrand]);

  const fetchListSegments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getListSegments(buildFilters());
      console.log('API Response:', response.data);
      
      // Get currently selected segments from localStorage
      let selectedSegments = [];
      try {
        const savedSegments = localStorage.getItem('selectedSegments');
        if (savedSegments) {
          selectedSegments = JSON.parse(savedSegments);
        }
      } catch (error) {
        console.error('Error loading selected segments from localStorage:', error);
      }
      
      // Handle the new flat array response format
      const allSegments = [];
      
      // Check if response.data is an array (new format) or has the old nested structure
      if (Array.isArray(response.data)) {
        // New format: flat array of segment objects
        response.data.forEach((segment) => {
          const newSegment = {
            id: `${segment.list_name}-${segment.segment_name}`,
            list_name: segment.list_name,
            segment_name: segment.segment_name,
            mailed_quantity: segment.mailed_quantity,
            // Handle potentially missing fields with defaults
            total_performance: segment.total_performance || segment.performance,
            usage_count: segment.usage_count || 1,
            products: Array.isArray(segment.products) ? segment.products : [segment.products],
            // Add new fields from API response
            gross_orders: segment.gross_orders || 0,
            gross_percentage: segment.gross_percentage || 0,
            net_profit_loss: segment.net_profit_loss || 0,
            net_profit_loss_per_buyer: segment.net_profit_loss_per_buyer || 0,
            be_pl_per_buyer_ltv: segment.be_pl_per_buyer_ltv || 0,
            fe_pl_per_buyer_ltv: segment.fe_pl_per_buyer_ltv || 0,
            performance_field: segment.performance_field || 'net_profit_loss',
            delta: segment.delta || 0,
          };
          
          // Check if this segment is already selected
          const isAlreadySelected = selectedSegments.some(s => 
            s.list_name === newSegment.list_name && 
            s.segment_name === newSegment.segment_name && 
            s.mailed_quantity === newSegment.mailed_quantity
          );
          
          // Only add if not already selected
          if (!isAlreadySelected) {
            allSegments.push(newSegment);
          }
        });
      } else {
        // Old format: nested structure with lists containing segments
        response.data.forEach((list) => {
          list.segments.forEach((segment) => {
            const newSegment = {
              id: `${list.list_name}-${segment.segment_name}`,
              list_name: list.list_name,
              segment_name: segment.segment_name,
              mailed_quantity: segment.mailed_quantity,
              total_performance: segment.total_performance,
              usage_count: segment.usage_count,
              products: segment.products,
              delta: segment.delta || 0,
            };
            
            // Check if this segment is already selected
            const isAlreadySelected = selectedSegments.some(s => 
              s.list_name === newSegment.list_name && 
              s.segment_name === newSegment.segment_name && 
              s.mailed_quantity === newSegment.mailed_quantity
            );
            
            // Only add if not already selected
            if (!isAlreadySelected) {
              allSegments.push(newSegment);
            }
          });
        });
      }

      // Sort segments by performance (highest first)
      const sortedSegments = [...allSegments].sort((a, b) => b.net_profit_loss - a.net_profit_loss);
      
      setSegments(sortedSegments);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch segment data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [buildFilters]);

  const handleResetSelection = () => {
    // Clear selected segments
    localStorage.removeItem('selectedSegments'); // Keep for backward compatibility
    
    if (selectedCampaigns) {
      // Clear only the selected campaign's segments
      const campaignSegments = JSON.parse(localStorage.getItem('campaignSegments') || '{}');
      if (campaignSegments[selectedCampaigns]) {
        delete campaignSegments[selectedCampaigns];
        localStorage.setItem('campaignSegments', JSON.stringify(campaignSegments));
        setCampaignListSelections(campaignSegments);
      }
    } else {
      // Clear all campaign segments if no campaign is selected
      localStorage.removeItem('campaignSegments');
      setCampaignListSelections({});
    }
    
    setUpdateCounter(prev => prev + 1);
  };
  const fetchCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const response = await getLightweightMediaPlans();
      setCampaigns(response.data.results);
    } catch (err) {
      message.error('Failed to fetch campaigns');
      console.error('Error fetching campaigns:', err);
    } finally {
      setLoadingCampaigns(false);
    }
  };

 

  const handleCampaignSelect = (campaignIds) => {
    setSelectedCampaigns(campaignIds);
    setActiveCampaign(campaignIds);
    
    // Load segments for the selected campaign
    loadCampaignSegments(campaignIds);
  };
  
  // Function to load segments for a specific campaign
  const loadCampaignSegments = (campaignId) => {
    if (!campaignId) return;
    
    try {
      const savedCampaignSegments = localStorage.getItem('campaignSegments');
      if (savedCampaignSegments) {
        const campaignSegments = JSON.parse(savedCampaignSegments);
        setCampaignListSelections(campaignSegments);
      }
    } catch (error) {
      console.error('Error loading campaign segments:', error);
    }
  };
  
 
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    render: (text) => text,
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const handleSelectSegment = (record) => {
    console.log('Selected segment:', record);
    
    if (!selectedCampaigns) {
      message.warning('Please select a campaign first');
      return;
    }
    
    // Add segment to localStorage with campaign association
    const added = addSegmentToCampaign(record, selectedCampaigns);
    
    if (added) {
      message.success(`Added segment: ${record.segment_name} to campaign`);
      // Increment update counter to trigger SelectedSegmentSummary reload
      setUpdateCounter(prev => prev + 1);
    } else {
      message.info(`Segment ${record.segment_name} is already selected for this campaign`);
    }
  };
  
  // Function to add segment to a specific campaign
  const addSegmentToCampaign = (segment, campaignId) => {
    try {
      // Get campaign-based segments from localStorage
      let campaignSegments = {};
      const savedCampaignSegments = localStorage.getItem('campaignSegments');
      
      if (savedCampaignSegments) {
        campaignSegments = JSON.parse(savedCampaignSegments);
      }
      
      // Find selected campaign metadata
      const campaignMeta = campaigns?.find?.((c) => c.id === campaignId);
      const resolvedCampaignName = campaignMeta?.campaign_code || `Campaign ${campaignId}`;
      const resolvedCampaignCode = campaignMeta?.campaign_code || null;
      
      // Initialize or normalize storage for this campaign
      if (!campaignSegments[campaignId]) {
        campaignSegments[campaignId] = {
          campaignName: resolvedCampaignName,
          campaign_code: resolvedCampaignCode,
          segments: [],
        };
      } else if (Array.isArray(campaignSegments[campaignId])) {
        // Normalize legacy array shape to object-with-segments
        campaignSegments[campaignId] = {
          campaignName: resolvedCampaignName,
          campaign_code: resolvedCampaignCode,
          segments: campaignSegments[campaignId],
        };
      } else {
        // Ensure metadata present
        campaignSegments[campaignId].campaignName = campaignSegments[campaignId].campaignName || resolvedCampaignName;
        if (campaignSegments[campaignId].campaign_code === undefined) {
          campaignSegments[campaignId].campaign_code = resolvedCampaignCode;
        }
        if (!Array.isArray(campaignSegments[campaignId].segments)) {
          campaignSegments[campaignId].segments = [];
        }
      }
      
      // Check if segment is already in this campaign's list
      const isAlreadySelected = (campaignSegments[campaignId].segments || []).some(s => 
        s.list_name === segment.list_name && 
        s.segment_name === segment.segment_name && 
        s.mailed_quantity === segment.mailed_quantity
      );
      
      if (isAlreadySelected) {
        return false;
      }
      
      // Add segment to this campaign's list
      campaignSegments[campaignId].segments.push(segment);
      
      // Save back to localStorage
      localStorage.setItem('campaignSegments', JSON.stringify(campaignSegments));
      
      // Update state to reflect changes
      setCampaignListSelections(campaignSegments);
      
      return true;
    } catch (error) {
      console.error('Error saving segment to campaign:', error);
      return false;
    }
  };
  
  // Fetch options for dropdowns
  const fetchDropdownOptions = useCallback(async () => {

await fetchCampaigns();
    setLoadingOptions(true);
    try {
      
      // Fetch products
      const productsResponse = await getProducts();
      const productOpts = productsResponse.data.map(product => ({
        label: product.name,
        value: product.id,
      }));

      setProductOptions(productOpts);
      
      // Fetch categories
      const categoriesResponse = await getCategories();
      const categoryOpts = categoriesResponse.data.map(category => ({
        label: category.name,
        value: category.name,
      }));
      setCategoryOptions(categoryOpts);
    } catch (err) {
      console.error('Error fetching dropdown options:', err);
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  const columns = [
    {
      title: 'List Name',
      dataIndex: 'list_name',
      key: 'list_name',
      ...getColumnSearchProps('list_name'),
    },
    {
      title: 'Segment Name',
      dataIndex: 'segment_name',
      key: 'segment_name',
      ...getColumnSearchProps('segment_name'),
    },
    {
      title: 'Mailed Quantity',
      dataIndex: 'mailed_quantity',
      key: 'mailed_quantity',
      sorter: (a, b) => a.mailed_quantity - b.mailed_quantity,
      render: (text) => text.toLocaleString(),
    },
    {
      title: 'Gross Orders',
      dataIndex: 'gross_orders',
      key: 'gross_orders',
      sorter: (a, b) => a.gross_orders - b.gross_orders,
      render: (text) => text.toLocaleString(),
    },
    {
      title: 'Gross %',
      dataIndex: 'gross_percentage',
      key: 'gross_percentage',
      sorter: (a, b) => a.gross_percentage - b.gross_percentage,
      render: (text) => (parseFloat(text) ).toFixed(2) + '%',
    },
    {
      title: 'Net P/L',
      dataIndex: 'net_profit_loss',
      key: 'net_profit_loss',
      sorter: (a, b) => a.net_profit_loss - b.net_profit_loss,
      render: (text) => (
        <Tag color={parseFloat(text) > 0 ? 'green' : 'red'}>${parseFloat(text).toFixed(2)}</Tag>
      ),
    },
    {
      title: 'Net P/L per Buyer',
      dataIndex: 'net_profit_loss_per_buyer',
      key: 'net_profit_loss_per_buyer',
      sorter: (a, b) => a.net_profit_loss_per_buyer - b.net_profit_loss_per_buyer,
      render: (text) => (
        <Tag color={parseFloat(text) > 0 ? 'green' : 'red'}>${parseFloat(text).toFixed(2)}</Tag>
      ),
    },
    {
      title: 'LTV FE P/L per Buyer',
      dataIndex: 'fe_pl_per_buyer_ltv',
      key: 'fe_pl_per_buyer_ltv',
      sorter: (a, b) => a.fe_pl_per_buyer_ltv - b.fe_pl_per_buyer_ltv,
      render: (text) => (
        <Tag color={parseFloat(text) > 0 ? 'green' : 'red'}>${parseFloat(text).toFixed(2)}</Tag>
      ),
    },
    {
      title: 'LTV BE P/L per Buyer',
      dataIndex: 'be_pl_per_buyer_ltv',
      key: 'be_pl_per_buyer_ltv',
      sorter: (a, b) => a.be_pl_per_buyer_ltv - b.be_pl_per_buyer_ltv,
      render: (text) => (
        <Tag color={parseFloat(text) > 0 ? 'green' : 'red'}>${parseFloat(text).toFixed(2)}</Tag>
      ),
    },

    {
      title: 'LTV Delta',
      dataIndex: 'delta',
      key: 'delta',
      sorter: (a, b) => a.delta - b.delta,
      render: (text) => (
        <Tag color={parseFloat(text) > 0 ? 'green' : 'red'}>${parseFloat(text).toFixed(2)}</Tag>
      ),
    },

 
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button type="primary" size="small" onClick={() => handleSelectSegment(record)}>
          Select
        </Button>
      ),
    },
  ];


  useEffect(() => {
    setBrands([]);
    setSelectedBrand(undefined);  
    setLoadingBrands(true);
    fetchBrandsBySelectedClients(selectedClients)
      .then((res) => {
        console.log("Brands:", res.data);
        if (!res.data || res.data.length === 0) return;
        setBrands(res.data);
        if (res.data.length === 1) {
          const autoSelectedBrand = res.data[0].id;
          setSelectedBrand(autoSelectedBrand);
          fetchListSegments();
          fetchDropdownOptions();
        }
        setLoadingBrands(false);
      })
      .catch(() => {
        setLoadingBrands(false);
        console.error("Failed to fetch brands.");
      });
      
    // Load campaign segments from localStorage on component mount
    try {
      const savedCampaignSegments = localStorage.getItem('campaignSegments');
      if (savedCampaignSegments) {
        setCampaignListSelections(JSON.parse(savedCampaignSegments));
      }
    } catch (error) {
      console.error('Error loading campaign segments:', error);
    }
  }, []);
 
  return (
    <>
      <SelectedSegmentSummary onReset={handleResetSelection} forceUpdate={updateCounter} />
      <Divider style={{ margin: '24px 0 16px' }} />
      <Card
        title="Campaign List Selection"
        bordered={false}
        extra={<Button onClick={fetchListSegments}>⟳ Refresh</Button>}
      >
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
        <Row gutter={16} style={{  }}>
          <Col span={12}>
            <Form.Item label="Select Campaigns">
              <Select
                placeholder="Select campaigns"
                value={selectedCampaigns}
                onChange={handleCampaignSelect}
                style={{ width: '100%' }}
                loading={loadingCampaigns}
                allowClear
              >
                {campaigns.map(campaign => (
                  <Option key={campaign.id} value={campaign.id}>
                    {campaign.campaign_code || 'Campaign'} - {campaign.date}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
 
        </Row>
 
        
        <Divider style={{ margin: '12px 0 16px' }} />
 
          <Form layout="vertical">
          <Row gutter={16}>
            
                        <Col span={6}>
              <Form.Item label="Brand">
                <Select
                  placeholder="Select brand"
                  value={selectedBrand}
                  onChange={setSelectedBrand}
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  loading={loadingBrands}
                  options={brands.map((brand) => ({
                    label: brand.brand_name,
                    value: brand.id,
                  }))}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item label="Product">
                <Select
                  placeholder="Select product"
                  value={product}
                  onChange={setProduct}
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  loading={loadingOptions}
                  options={productOptions}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item label="Category">
                <Select
                  placeholder="Select category"
                  value={category}
                  onChange={setCategory}
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  loading={loadingOptions}
                  options={categoryOptions}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Performance Metric">
                <Select
                  options={performanceOptions}
                  value={performanceField}
                  onChange={setPerformanceField}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>

          </Row>
          <Row>
            <Col>
              <Button 
                type="primary" 
                onClick={fetchListSegments} 
                style={{ marginRight: 8 }}
                loading={loading}
              >
                Apply Filters
              </Button>
 
              <Button 
                onClick={() => {
                  setProduct(undefined);
                  setCategory(undefined);
                  setPerformanceField('net_profit_loss');
                  setSelectedBrand(undefined);
                  setDateRange([]);
                }}
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Form>

        {error && <span style={{ color: 'red' }}>{error}</span>}

        <Table
          columns={columns}
          dataSource={segments}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 40 }}
        />

        </Space>
      </Card>
    </>
  );
};

export default ListSelection;
